import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { getFormatedBusinessDate } from "../../../utils/Functions/table";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/User/CustomerSubscriptions/customerSubscriptionSlice";
import {
  useGetCustomerSubscriptionListDataQuery,
  useUpdateCustomerSubscriptionTableHeadDataMutation,
} from "../../../store/queries/Subscription";

const useCustomerSubscriptions = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const dispatch = useDispatch();
  const subscriptionState = useSelector((state) => state.customerSubscription);
  const { showEditModal } = useSelector((state) => state.global);

  const {
    data: subscriptionList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetCustomerSubscriptionListDataQuery({
    page_size: subscriptionState?.currentPageSize,
    page: subscriptionState?.currentPage,
    sort: subscriptionState?.sortBy,
    sort_order: subscriptionState?.sortOrder,
    search: subscriptionState?.search,
    status: subscriptionState?.status,
  });

  const [updateSubscriptionFields] =
    useUpdateCustomerSubscriptionTableHeadDataMutation();

  useEffect(() => {
    if (subscriptionState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [subscriptionState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.status = "";
        state.currentPage = 1;
      })
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleSort = (label) => {
    if (subscriptionState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            subscriptionState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const openAction = (modal, subscription) => {
    dispatch(
      updateConfig((state) => {
        state[modal] = true;
        state.selectedId = subscription?.subscription_id ?? "";
        state.selectedSubscription = subscription ?? {};
      })
    );
  };

  const closeAction = (modal) => {
    dispatch(
      updateConfig((state) => {
        state[modal] = false;
      })
    );
  };

  const handleSettingModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showSettingModal = !state.showSettingModal;
      })
    );
  };

  const getRow = (field, data) => {
    // Every date this module can show, not just the one column that ships visible - the
    // other three are a tick away in Edit Columns and would print the raw timestamp.
    const dateCell = (field, data) => (
      <p className="pro-mb-0">{getFormatedBusinessDate(data?.[field])}</p>
    );

    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      expires_at: dateCell,
      purchased_at: dateCell,
      activated_at: dateCell,
      created_at: dateCell,
      remaining_items: (field, data) => (
        <p className="pro-mb-0">
          {data?.remaining_items} of {data?.total_items}
        </p>
      ),
      action: (field, data) => (
        <div className="pro-d-flex pro-gap-2 no-wrap">
          <Button
            className={"pro-btn-link sm"}
            onClick={() => openAction("showDetailsModal", data)}
          >
            View
          </Button>
          {/*
            Cancelling and crediting are only offered where they mean something - a
            cancelled subscription has already had its balance written off and its refund
            settled, and reopening that conversation from a button would only confuse it.
          */}
          {data?.status_label !== "Cancelled" && (
            <>
              <Button
                className={"pro-btn-link sm"}
                onClick={() => openAction("showCreditModal", data)}
              >
                Credit
              </Button>
              <Button
                className={"pro-btn-link sm"}
                onClick={() => openAction("showCancelModal", data)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  return {
    isLoading,
    isFetching,
    showEditModal,
    subscriptionList,
    subscriptionState,
    paginationOptions,
    updateSubscriptionFields,
    handleEditColumnsClick,
    handleSettingModal,
    handlePagination,
    handleClearClick,
    handlePageSize,
    handleSearch,
    closeAction,
    handleSort,
    refetch,
    getRow,
  };
};

export default useCustomerSubscriptions;
